'use client';

import Link from 'next/link';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLocalePath } from '@/hooks/use-locale';
import {
  formatCapturedDate,
  normalizeRegisterCourt,
} from './companyDetailUtils';

type SourceAttributionProps = {
  source: 'unternehmensregister' | 'handelsregister';
  registerCourt?: string | null;
  registerId?: string | null;
  /** Generic company-row update timestamp, not a register retrieval time. */
  capturedAt?: string | null;
  className?: string;
};

/** Visible provenance for company facts. Keep this separate from JSON-LD:
 * readers should be able to verify the source without inspecting markup. */
export function SourceAttribution({
  source,
  registerCourt,
  registerId,
  capturedAt,
  className,
}: SourceAttributionProps) {
  const { t, i18n } = useTranslation();
  const localePath = useLocalePath();
  const label = t(`companyDetail.sources.${source}`);
  const court = normalizeRegisterCourt(registerCourt);
  const normalizedRegisterId = registerId?.trim() || null;
  const registerDetails =
    court && normalizedRegisterId
      ? t('companyDetail.sources.courtAndRegisterId', {
          court,
          registerId: normalizedRegisterId,
        })
      : court
        ? t('companyDetail.sources.court', { court })
        : normalizedRegisterId;
  const capturedDate = formatCapturedDate(capturedAt, i18n.language);

  return (
    <p
      className={cn(
        'mt-3 border-t border-border/60 pt-2 text-[11px] leading-relaxed text-muted-foreground',
        className,
      )}
    >
      {t('companyDetail.sources.label')}:{' '}
      <Link
        href={localePath('/sources')}
        className="inline-flex items-center gap-1 underline decoration-border underline-offset-2 hover:text-foreground"
      >
        {label}
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </Link>
      {registerDetails ? ` ${registerDetails}` : null}
      {capturedDate
        ? ` ${t('companyDetail.sources.asOf', { date: capturedDate })}`
        : null}
    </p>
  );
}
